import { Sun, Moon, ArrowLeft } from "@phosphor-icons/react";
import { T } from "../themes";
import { useRef, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// ── App Switcher Pill ────────────────────────────────────────────────────────
function AppSwitcher({ currentApp }) {
  const liftActive = currentApp === "lift";
  const navigate = useNavigate();
  const go = (path) => { try { navigate(path); } catch { window.location.href = path; } };
  return (
    <div style={{
      display:"flex", background:T.card,
      borderRadius:20, padding:3, gap:2,
      border:`1px solid ${T.surfaceMid}`,
    }}>
      <div
        onClick={liftActive ? undefined : ()=>{ go("/workout"); }}
        style={{
          background: liftActive ? T.liftRed : "transparent",
          borderRadius:16, padding:"4px 7px",
          display:"flex", alignItems:"center", gap:4,
          cursor: liftActive ? "default" : "pointer",
        }}
      >
        <svg width="10" height="10" viewBox="0 0 24 24"
          fill={liftActive ? T.tileText : T.inkMuted} stroke="none">
          <path d="M13 2L4.5 13.5H11L10 22l9.5-12H13.5L13 2z"/>
        </svg>
        <span style={{
          fontSize:10, fontWeight:700,
          color: liftActive ? T.tileText : T.inkMuted,
          fontFamily:T.fontDisplay, letterSpacing:"0.3px",
        }}>Lift</span>
      </div>
      <div
        onClick={!liftActive ? undefined : ()=>{ go("/"); }}
        style={{
          background: !liftActive ? T.nutriGreen : "transparent",
          borderRadius:16, padding:"4px 10px",
          display:"flex", alignItems:"center", gap:4,
          cursor: !liftActive ? "default" : "pointer",
        }}
      >
        <svg width="10" height="10" viewBox="0 0 24 24"
          fill={!liftActive ? "#fff" : "none"}
          stroke={!liftActive ? "none" : T.inkMuted}
          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22C8 22 5 18 5 14c0-5 4-12 7-12s7 7 7 12c0 4-3 8-7 8z"/>
          <path d="M12 3c0 0-1 3-1 6" fill="none" stroke={!liftActive ? "#fff" : T.inkMuted} strokeWidth="2"/>
        </svg>
        <span style={{
          fontSize:10, fontWeight:700,
          color: !liftActive ? "#fff" : T.inkMuted,
          fontFamily:T.fontDisplay, letterSpacing:"0.3px",
        }}>Nutri</span>
      </div>
    </div>
  );
}

// ── Shared App Header ────────────────────────────────────────────────────────
// Props:
//   title (string)        — screen title, e.g. "Today", "Intel"
//   subtitle (string)     — secondary line, e.g. date, "AI insights & audit"
//   persona (string)      — current persona (unused for now, reserved)
//   darkMode (bool)       — current dark mode state
//   onToggleDarkMode (fn) — toggle handler
//   currentApp (string)   — "nutri" or "lift"
//   onAvatarPress (fn?)   — avatar tap handler
//   avatarUrl (string?)   — profile image (future)
//   username (string?)    — for avatar initial fallback
//   rightContent (node?)  — extra elements (refresh btn, CSV export, month nav)
//   showBackButton (bool?)— show ArrowLeft
//   onBackPress (fn?)     — back button handler
//   subContent (node?)    — full-width row rendered below the main header row, inside fixed container
export default function AppHeader({
  title,
  subtitle,
  persona,
  darkMode,
  onToggleDarkMode,
  currentApp,
  onAvatarPress,
  avatarUrl,
  username,
  rightContent,
  showBackButton = false,
  onBackPress,
  subContent,
}) {
  const initial = (username?.[0] || "U").toUpperCase();
  const headerRef = useRef(null);
  const [headerHeight, setHeaderHeight] = useState(76);
  useEffect(()=>{
    const el = headerRef.current;
    if(!el) return;
    const ro = new ResizeObserver(entries=>{
      setHeaderHeight(entries[0].contentRect.height);
    });
    ro.observe(el);
    return ()=>ro.disconnect();
  },[]);

  return (
    <>
    <div ref={headerRef} style={{ background:T.pageBg, position:"fixed", top:0, left:0, right:0, zIndex:100 }}>
      {/* ── Top row ──────────────────────────────────────────────── */}
      <div style={{
        padding:"16px 16px 12px",
        display:"flex",
        alignItems:"center",
        justifyContent:"space-between",
      }}>
        {/* Left: back? + logo + title/subtitle */}
        <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:0 }}>
          {showBackButton && (
            <button onClick={onBackPress} style={{
              width:32, height:32, borderRadius:"50%",
              background:T.surfaceLight, border:"none",
              cursor:"pointer", display:"flex",
              alignItems:"center", justifyContent:"center",
              flexShrink:0,
            }}>
              <ArrowLeft size={16} weight="bold" color={T.inkDark}/>
            </button>
          )}
          <img src="/logo192.png" alt="LF" width={38} height={38}
            style={{ flexShrink:0, borderRadius:11, objectFit:"cover" }}/>
          <div style={{ minWidth:0 }}>
            <div style={{
              fontFamily:T.fontDisplay, fontSize:30, fontWeight:800,
              color:T.inkDark, letterSpacing:"-0.5px", lineHeight:1,
              overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
            }}>{title}</div>
            <div style={{
              fontSize:11, fontWeight:500,
              color:T.inkMuted, fontFamily:T.fontSans,
              marginTop:2, whiteSpace:"nowrap",
              overflow:"hidden", textOverflow:"ellipsis",
            }}>{subtitle}</div>
          </div>
        </div>

        {/* Right: extras + switcher + dark toggle + avatar */}
        <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
          {rightContent}
          <AppSwitcher currentApp={currentApp}/>
          {/* Dark mode toggle — hidden on standalone pages that don't support dark mode */}
          {onToggleDarkMode && (
            <button onClick={onToggleDarkMode} style={{
              width:44, height:44, borderRadius:22,
              background: darkMode ? `${T.orange}22` : "transparent",
              border:"none", cursor:"pointer",
              display:"flex", alignItems:"center", justifyContent:"center",
              margin:"-8px 0",
            }}>
              {darkMode
                ? <Sun size={16} weight="fill" color={T.orange}/>
                : <Moon size={16} weight="fill" color={T.textSub}/>
              }
            </button>
          )}
          {/* Profile avatar */}
          <div onClick={onAvatarPress} style={{
            width:44, height:44, borderRadius:"50%",
            background:"transparent",
            display:"flex", alignItems:"center", justifyContent:"center",
            cursor: onAvatarPress ? "pointer" : "default",
            flexShrink:0,
            margin:"-6px 0",
          }}>
            <div style={{
              width:32, height:32, borderRadius:"50%",
              background:T.primary,
              display:"flex", alignItems:"center", justifyContent:"center",
            }}>
              {avatarUrl
                ? <img src={avatarUrl} alt="" width={32} height={32}
                    style={{ borderRadius:"50%", objectFit:"cover" }}/>
                : <span style={{
                    fontFamily:T.fontDisplay, fontSize:12, fontWeight:700,
                    color:"#fff", lineHeight:1,
                  }}>{initial}</span>
              }
            </div>
          </div>
        </div>
      </div>
      {subContent&&(
        <div style={{borderTop:`1px solid ${T.cardBorder}`}}>
          {subContent}
        </div>
      )}
    </div>
    <div style={{height:headerHeight,flexShrink:0}}/>
    </>
  );
}
